import { ErrorMessage, Field } from "formik";
import { IMAGE_URL } from "../../config";
import cameraIcon from "../../assets/icons/camera.svg";
import get from "lodash/get";
import set from "lodash/set";
import { useState } from "react";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormImageField = ({
  label = "imageField",
  labelClassName = "",
  name = "imageField",
  className = "",
  previewClassName = "",
  camera = false,
  maxSize = 5, // Default to 5MB
  ...props
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event, form, name) => {
    const file = event.target.files[0];
    if (!file) return;

    // --- Dynamic Size Validation ---
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      alert(`File is too large! Maximum size allowed is ${maxSize}MB.`);
      event.target.value = null; // Reset input
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    // Progress and Life-cycle handlers...
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;
        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `https://api.requal.in${filePath}`;

        // Using formik's setFieldValue is cleaner than copying the whole object
        form.setFieldValue(name, fullUrl);
        console.log("Upload success:", fullUrl);
      } else {
        alert("Image upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleRemoveImage = (form, name) => {
    form.setFieldValue(name, "");
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const file = get(form.values, name);
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-1 ${className} w-full my-5`}>
            {label && (
              <label
                htmlFor={name}
                className={`text-fs_14 md:text-fs_16 font-fw_400 capitalize  ${
                  error ? "text-errorColor" : labelClassName
                }`}
              >
                {formatUnderscoreToSpace(label)}
              </label>
            )}

            <div className="md:w-[8rem] w-[5rem] md:h-[8.3rem] h-[5.3rem] relative overflow-hidden">
              {file ? (
                <div className="relative">
                  <img
                    src={file}
                    alt="Uploaded"
                    className={`md:w-[8rem] w-[5rem] md:h-[8rem] h-[5rem] object-cover rounded-lg ${previewClassName}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(form, name)}
                    className="absolute top-0 right-0 bg-gray-400 text-white rounded-full p-[1px] px-[6px] text-xs hover:bg-gray-600"
                  >
                    x
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <div
                    className={`border-[1.5px] md:w-[8rem] w-[5rem] md:h-[8rem] h-[5rem] flex items-center justify-center rounded-lg my-1 bg-white ${
                      error ? "border-errorColor" : "border-greyText"
                    }`}
                  >
                    {isUploading ? (
                      <div className="text-sm text-primary font-bold">
                        {uploadProgress}%
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <img
                          src={cameraIcon}
                          alt="Camera"
                          width={24}
                          height={24}
                        />
                        <p className="text-greyText text-fs_12 font-fw_400 text-center mt-2">
                          Upload
                        </p>
                      </div>
                    )}
                  </div>
                  <input
                    name={name}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture={camera ? "environment" : undefined}
                    onChange={(event) => handleFileChange(event, form, name)}
                  />
                </label>
              )}
            </div>
            {error && (
              <label className="text-errorColor text-fs_14 ps-1 h-[1rem]">
                <ErrorMessage name={name} />
              </label>
            )}
          </div>
        );
      }}
    </Field>
  );
};
